import React, { useState, useEffect, useRef, useCallback } from "react"
import DEMO_PROMPTS from "./data/demoPrompts"

const ADJACENT_KEYS = {
  a: "sq",
  b: "vn",
  c: "xv",
  d: "sf",
  e: "wr",
  f: "dg",
  g: "fh",
  h: "gj",
  i: "uo",
  j: "hk",
  k: "jl",
  l: "k",
  m: "n",
  n: "bm",
  o: "ip",
  p: "o",
  q: "w",
  r: "et",
  s: "ad",
  t: "ry",
  u: "yi",
  v: "cb",
  w: "qe",
  x: "zc",
  y: "tu",
  z: "x",
}

const TYPO_CHANCE = 0.05
const TYPO_PAUSE_MS = 300
const BACKSPACE_MS = 40
const PUNCTUATION = new Set([",", ".", "!", "?"])

const HOLD_DURATION_MS = 4000
const FADE_DURATION_MS = 300
const INTER_PROMPT_GAP_MS = 500
const POST_TYPING_PAUSE_MS = 800
const REDUCED_MOTION_CYCLE_MS = 6000
const BASE_TYPING_MS = 50
const TYPING_JITTER_MS = 80
const SPACE_PAUSE_MS = 150
const PUNCTUATION_PAUSE_MS = 300

const TAG_CLASS_MAP = {
  "Smart AI": "demo-tag-smart-ai",
  "Web Search": "demo-tag-web-search",
  "Precise Location": "demo-tag-precise-location",
}

const getTypingDelay = (char, prevChar) => {
  const base = BASE_TYPING_MS + Math.random() * TYPING_JITTER_MS
  if (prevChar === " ") return base + SPACE_PAUSE_MS
  if (PUNCTUATION.has(prevChar)) return base + PUNCTUATION_PAUSE_MS
  return base
}

const getAdjacentKey = (char) => {
  const lower = char.toLowerCase()
  const options = ADJACENT_KEYS[lower]
  if (!options) return null
  const typo = options[Math.floor(Math.random() * options.length)]
  return char === char.toUpperCase() ? typo.toUpperCase() : typo
}

const prefersReducedMotion = () =>
  typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches

const DemoShowcase = ({ isActive, onChoicesReady, startComplete }) => {
  const [promptIndex, setPromptIndex] = useState(0)
  const [displayedText, setDisplayedText] = useState(startComplete ? DEMO_PROMPTS[0].prompt : "")
  const [fading, setFading] = useState(false)
  const timerRef = useRef(null)
  const activeRef = useRef(isActive)
  const startCompleteRef = useRef(startComplete)

  useEffect(() => {
    activeRef.current = isActive
  }, [isActive])

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }, [])

  useEffect(() => {
    if (!isActive) clearTimer()
    return clearTimer
  }, [isActive, clearTimer])

  useEffect(() => {
    if (!isActive || !prefersReducedMotion()) return

    const prompt = DEMO_PROMPTS[promptIndex]
    setDisplayedText(prompt.prompt)

    // Skip onChoicesReady for startComplete — parent already has the slices
    if (!(promptIndex === 0 && startCompleteRef.current)) {
      onChoicesReady(prompt.choices, prompt.prompt)
    }
    if (promptIndex === 0) startCompleteRef.current = false

    const id = setTimeout(() => {
      if (!activeRef.current) return
      setPromptIndex((i) => (i + 1) % DEMO_PROMPTS.length)
    }, REDUCED_MOTION_CYCLE_MS)

    return () => clearTimeout(id)
  }, [isActive, promptIndex]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isActive || prefersReducedMotion()) return

    const prompt = DEMO_PROMPTS[promptIndex]
    const target = prompt.prompt
    let cancelled = false

    const isLive = () => !cancelled && activeRef.current
    // Every step re-checks liveness when it fires, so a step queued before the
    // demo stopped never runs.
    const schedule = (fn, ms) => {
      if (!isLive()) return
      timerRef.current = setTimeout(() => isLive() && fn(), ms)
    }

    // Hold phase: wait for reading, then fade out and advance
    const startHoldPhase = () => {
      schedule(() => {
        setFading(true)

        schedule(() => {
          setFading(false)
          setDisplayedText("")

          schedule(() => setPromptIndex((i) => (i + 1) % DEMO_PROMPTS.length), INTER_PROMPT_GAP_MS)
        }, FADE_DURATION_MS)
      }, HOLD_DURATION_MS)
    }

    // First prompt with startComplete: skip typing, go directly to hold
    if (promptIndex === 0 && startCompleteRef.current) {
      startCompleteRef.current = false
      setDisplayedText(target)
      startHoldPhase()
      return () => {
        cancelled = true
        clearTimer()
      }
    }

    // Normal typing engine
    let pos = 0
    let current = ""

    const typeNext = () => {
      if (pos >= target.length) {
        // Typing complete — pause then show choices
        schedule(() => {
          onChoicesReady(prompt.choices, prompt.prompt)
          startHoldPhase()
        }, POST_TYPING_PAUSE_MS)
        return
      }

      const char = target[pos]
      const prevChar = pos > 0 ? target[pos - 1] : ""

      // Maybe introduce a typo
      if (char.match(/[a-zA-Z]/) && Math.random() < TYPO_CHANCE) {
        const typoChar = getAdjacentKey(char)
        if (typoChar) {
          current += typoChar
          setDisplayedText(current)

          // Pause, then backspace, then correct
          schedule(() => {
            current = current.slice(0, -1)
            setDisplayedText(current)

            schedule(() => {
              current += char
              pos++
              setDisplayedText(current)
              schedule(typeNext, getTypingDelay(char, prevChar))
            }, BACKSPACE_MS)
          }, TYPO_PAUSE_MS)
          return
        }
      }

      current += char
      pos++
      setDisplayedText(current)
      schedule(typeNext, getTypingDelay(char, prevChar))
    }

    schedule(typeNext, 0)

    return () => {
      cancelled = true
      clearTimer()
    }
  }, [isActive, promptIndex]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!isActive) return null

  const currentPrompt = DEMO_PROMPTS[promptIndex]

  return (
    <div className={`demo-showcase${fading ? " demo-fading" : ""}`} aria-live="polite">
      <div className="demo-typing-text">{displayedText}</div>
      <div className="demo-tags">
        {currentPrompt.tags.map((tag) => (
          <span key={tag} className={`demo-tag ${TAG_CLASS_MAP[tag] || ""}`} aria-label={`Feature: ${tag}`}>
            {tag}
          </span>
        ))}
      </div>
    </div>
  )
}

export default DemoShowcase
